import { Assets } from 'pixi.js';

/**
 * A map's ambient files in flight, by URL, so the overview can start them
 * while it glides into a world and the map that opens takes what has
 * already arrived (or is on its way) instead of asking again. Without
 * this the map fetched `ambient.json`, then the sheet's json, then its
 * image, one after another, on the opening screen.
 *
 * A file that fails is forgotten at once, so the next ask tries again; the
 * promise a caller holds still rejects, and that caller decides what a
 * failed load means (the map keeps its painting).
 */
const started = new Map<string, Promise<unknown>>();

/** One Pixi asset (a sheet's json, a picture, a mask), started once. */
export function ambientAsset<T>(url: string): Promise<T> {
  let p = started.get(url);
  if (!p) {
    const mine: Promise<unknown> = Assets.load<T>(url);
    started.set(url, mine);
    mine.catch(() => {
      if (started.get(url) === mine) started.delete(url);
    });
    p = mine;
  }
  return p as Promise<T>;
}

/** A folder's ambient.json, fetched once. */
export function ambientJson(base: string): Promise<unknown> {
  const url = `${base}/ambient.json`;
  let p = started.get(url);
  if (!p) {
    const mine: Promise<unknown> = fetch(url).then((res) => {
      if (!res.ok) throw new Error(`ambient.json: ${res.status}`);
      return res.json() as Promise<unknown>;
    });
    started.set(url, mine);
    mine.catch(() => {
      if (started.get(url) === mine) started.delete(url);
    });
    p = mine;
  }
  return p;
}

/**
 * Start a folder's files at once: its ambient.json and every file it is
 * known to hold (`files`, relative to the folder: the sheets' json and
 * picture and the masks). A hint that proves wrong costs one failed
 * request and nothing else: the map loads what ambient.json names.
 */
export function warmAmbient(base: string, files: readonly string[] = []): void {
  void ambientJson(base).catch(() => undefined);
  for (const f of files) void ambientAsset(`${base}/${f}`).catch(() => undefined);
}

/** The map that held a folder's files let them go: they are unloaded and forgotten. */
export function forgetAmbient(base: string, urls: readonly string[]): void {
  started.delete(`${base}/ambient.json`);
  for (const url of urls) {
    started.delete(url);
    void Assets.unload(url).catch(() => undefined);
  }
}
